import { Replay } from '../components/Replay.tsx';
import { RoundRefunds } from '../components/Refunds.tsx';
import { useId, useState } from 'react';
import { applyFilters, can, MIN_REASON, type OpsRound } from '@urbanrats/ops-core';
import { hashCanonical } from '@urbanrats/domain';
import { ChangeRequests } from '../components/ChangeRequests.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Link } from '../components/Link.tsx';
import { PageHead, Section, StateTag } from '../components/common.tsx';
import { fmtTime, hrefWith, param, useCtx } from '../lib.ts';

export function Rounds() {
  const { data, filters, search } = useCtx();
  const state = param(search, 'state');
  const rows = applyFilters(data, filters)
    .rounds.filter((r) => !state || r.state === state)
    .sort((a, b) => b.manifest.timing.bettingOpenAt.localeCompare(a.manifest.timing.bettingOpenAt));
  return (
    <>
      <PageHead title="Rounds">{state ? `State ${state}. ` : ''}Physical rounds shared by every operator; the manifest is locked before betting.</PageHead>
      <DataTable<OpsRound>
        testId="rounds-table"
        rows={rows}
        rowKey={(r) => r.gameRoundId}
        empty="No rounds match these filters."
        exportName="rounds"
        columns={[
          { key: 'id', header: 'Round', cell: (r) => <Link to={hrefWith(`/rounds/${r.gameRoundId}`, filters)}>{r.gameRoundId}</Link>, csv: (r) => r.gameRoundId },
          { key: 'game', header: 'Game', cell: (r) => r.gameId, csv: (r) => r.gameId },
          { key: 'cam', header: 'Camera', cell: (r) => r.cameraAlias, csv: (r) => r.cameraAlias },
          { key: 'mode', header: 'Source mode', cell: (r) => r.manifest.sourceMode, csv: (r) => r.manifest.sourceMode },
          { key: 'state', header: 'State', cell: (r) => <StateTag state={r.state} />, csv: (r) => r.state },
          { key: 'count', header: 'Count', numeric: true, cell: (r) => r.finalCount ?? '—', csv: (r) => r.finalCount ?? '' },
          { key: 'win', header: 'Winning', cell: (r) => r.winning.join(', ') || '—', csv: (r) => r.winning.join(' ') },
          { key: 'open', header: 'Betting opened', cell: (r) => fmtTime(r.manifest.timing.bettingOpenAt), csv: (r) => r.manifest.timing.bettingOpenAt },
        ]}
      />
    </>
  );
}

export function RoundDetail({ id }: { id: string }) {
  const { data, filters, user, backend, refresh } = useCtx();
  const r = data.rounds.find((x) => x.gameRoundId === id);
  const [decision, setDecision] = useState('CONFIRM_EXCLUDE');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const ids = { d: useId(), r: useId() };
  if (!r) return <p className="empty">Round not found in your scope.</p>;
  const hashOk = hashCanonical(r.manifest) === r.manifestHash;
  const bets = data.bets.filter((b) => b.bet.gameRoundId === r.gameRoundId);
  const pending = backend.requests().some((q) => q.kind === 'REVIEW_DECISION' && q.target === r.gameRoundId && q.status === 'PENDING');
  const submit = async () => {
    const payload: Record<string, string> = decision === 'VOID' ? { roundId: r.gameRoundId, decision: 'VOID' } : { roundId: r.gameRoundId, decision: 'CONFIRM', uncertain: decision === 'CONFIRM_COUNT' ? 'COUNT' : 'EXCLUDE' };
    const summary = decision === 'VOID' ? 'Void round and refund all stakes' : `Confirm result with uncertain crossings ${decision === 'CONFIRM_COUNT' ? 'counted' : 'excluded'} (evaluator recomputes)`;
    const res = await backend.request(user, 'REVIEW_DECISION', r.gameRoundId, summary, payload, reason);
    setMsg(res.ok ? `Request ${res.value.requestId} created — waiting for a second approver.` : `${res.error.code}: ${res.error.message}`);
    if (res.ok) setReason('');
    refresh();
  };
  return (
    <>
      <PageHead title={`Round ${r.gameRoundId}`}>
        {r.gameId} · camera {r.cameraAlias} · <StateTag state={r.state} /> {r.lastReason ? `· ${r.lastReason}` : ''}
      </PageHead>
      {backend.server && r.gameId === 'traffic-pulse' && <Replay roundId={r.gameRoundId} />}
      <RoundRefunds roundId={r.gameRoundId} state={r.state} />
      <Section title="Manifest (immutable)" testId="manifest">
        <p className={hashOk ? 'ok-text' : 'bad-text'} data-testid="manifest-hash">
          {hashOk ? '✓' : '✕'} sha256(manifest) {hashOk ? 'matches' : 'DOES NOT match'} the locked hash <span className="mono">{r.manifestHash}</span>
        </p>
        <details>
          <summary>Show manifest JSON</summary>
          <pre className="json">{JSON.stringify(r.manifest, null, 2)}</pre>
        </details>
      </Section>
      <Section title="Timeline" testId="timeline">
        <ol className="timeline">
          {r.timeline.map((t, i) => (
            <li key={i}>
              <span className="mono">{fmtTime(t.at)}</span> {t.from ? `${t.from} → ` : ''}
              <StateTag state={t.to} /> {t.reason && <span className="muted">{t.reason}</span>}
            </li>
          ))}
        </ol>
      </Section>
      <Section title="Video">
        {r.manifest.sourceMode === 'SIMULATION' ? (
          <p className="muted">SIMULATION round: crossings come from a fixture script; there is no video.</p>
        ) : (
          <p className="muted">
            {r.manifest.sourceMode} · window {(r.manifest.captureWindowPts?.startMicros ?? 0) / 1e6}–{(r.manifest.captureWindowPts?.endMicros ?? 0) / 1e6} s of epoch {r.manifest.sourceEpoch}
          </p>
        )}
      </Section>
      <Section title={`Crossings (${r.events.length})`} testId="crossings">
        {r.events.length === 0 ? (
          <p className="muted">No crossing events.</p>
        ) : (
          <ul className="plain">
            {r.events.map((e) => (
              <li key={e.eventId}>
                <span className="mono">{e.eventId}</span> {e.vehicleClass} {e.direction} conf {e.confidence.toFixed(2)} {e.uncertain && <span className="tag st-REVIEW_REQUIRED">uncertain</span>} <span className="muted">{e.origin}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Result" testId="result">
        <p>
          Count {r.finalCount ?? '—'} · winning {r.winning.join(', ') || '—'} · <Link to={hrefWith('/bets', filters, { round: r.gameRoundId })}>{bets.length} bets in your scope →</Link>
        </p>
      </Section>
      <Section title="Evidence" testId="evidence">
        {r.evidence ? (
          <dl className="kv">
            <dt>Status</dt>
            <dd>{r.evidence.status}</dd>
            <dt>Evidence verified</dt>
            <dd>{r.evidence.evidenceVerified ? 'yes' : 'no'}</dd>
            <dt>AI count independently confirmed</dt>
            <dd>{r.evidence.countIndependentlyConfirmed ? 'yes' : 'no'}</dd>
            <dt>Lightning seed</dt>
            <dd className="mono">{r.evidence.lightningReveal?.serverSeedHex ?? '—'}</dd>
          </dl>
        ) : (
          <p className="muted">No evidence bundle yet (available once a result exists).</p>
        )}
      </Section>
      <Section title="Review" testId="review">
        {r.state !== 'REVIEW_REQUIRED' ? (
          <p className="muted">No review needed for this round.</p>
        ) : can(user, 'request.review') && !pending ? (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <p className="note">
              A reviewer cannot pick a result. Options: void (refund all stakes), or confirm with the flagged uncertain crossings counted or excluded — the game&apos;s evaluator then recomputes the result. A second user must approve.
            </p>
            <label htmlFor={ids.d}>Decision</label>
            <select id={ids.d} value={decision} onChange={(e) => setDecision(e.target.value)} data-testid="review-decision">
              <option value="CONFIRM_EXCLUDE">Confirm — exclude uncertain crossings</option>
              <option value="CONFIRM_COUNT">Confirm — count uncertain crossings</option>
              <option value="VOID">Void and refund</option>
            </select>
            <label htmlFor={ids.r}>Reason (min {MIN_REASON} chars)</label>
            <textarea id={ids.r} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-reason" />
            <button type="submit" className="btn primary" data-testid="review-request">
              Request decision
            </button>
          </form>
        ) : (
          <p className="muted">{pending ? 'A decision request is pending approval.' : 'Your role cannot request review decisions.'}</p>
        )}
        {msg && (
          <p role="status" className="status-msg" data-testid="review-msg">
            {msg}
          </p>
        )}
        <ChangeRequests filter={(q) => q.kind === 'REVIEW_DECISION' && q.target === r.gameRoundId} />
      </Section>
    </>
  );
}
